import {Field, useField} from 'formik';
import React, {useEffect, useMemo} from 'react';

import {Box, Button, InputProps, TextField, Typography, FormControl} from '@mui/material';
import clsx from 'clsx';
import {FieldValue, AutoTextField} from './AutoFieldNew';
import {MoneyField, PercentField} from '../Common/SpecialCharacterTextField';
import {getPercentValidation} from '../../utils/currencyFormatter';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';
import {SaltusAny} from '../../utils/SaltusAny';
import {useAutoField} from './useAutoForm';

interface Props extends InputProps {
  field: AutoTextField;
  controlledValue: FieldValue | undefined;
  setControlledValue: React.Dispatch<React.SetStateAction<FieldValue | undefined>>;
  handleFieldSubmit: (value?: FieldValue) => void;
  emptyValText: string;
  readOnly: boolean;
  loading?: boolean;
  showClearButton?: boolean;
}

export default function AutoTextNew({
  controlledValue,
  setControlledValue,
  handleFieldSubmit,
  onBlur,
  readOnly,
  emptyValText: contextEmptyValText,
  loading = false,
  disabled,
  field,
  error,
  ...props
}: Props) {
  function defaultValidation(val: SaltusAny): string | null {
    if (field.requireSubmit) {
      return null;
    }
    if (field.percent) {
      const actualValue = field.requireSubmit ? controlledValue : formikField.value;
      const valueIncludingEmpty = readOnly
        ? actualValue || field.initialValue || field.emptyValText || contextEmptyValText
        : actualValue;
      if (valueIncludingEmpty) {
        return getPercentValidation(valueIncludingEmpty, 2);
      }
    }
    return null;
  }
  const options = field;
  const [formikField, formikMeta, formikHelpers] = useField({
    name: field.id,
    validate: (field.validation as any) || defaultValidation,
  });

  useEffect(() => {
    if (controlledValue) {
      if (field.requireSubmit) {
        const controlledError = defaultValidation(controlledValue);
        if (controlledError) {
          formikHelpers.setError(controlledError);
        }
      }
    }
  }, [controlledValue]);

  const actualValue = field.requireSubmit
    ? controlledValue ?? formikField.value
    : formikField.value;
  let valueIncludingEmpty = readOnly
    ? actualValue || field.initialValue || field.emptyValText || contextEmptyValText
    : field.canBeEmpty
    ? actualValue ?? field.initialValue
    : actualValue || field.initialValue;

  if (
    !field.allowNegative &&
    !Number.isNaN(valueIncludingEmpty) &&
    Number(valueIncludingEmpty) < 0
  ) {
    valueIncludingEmpty = String(Math.abs(valueIncludingEmpty));
  }
  const customTextField = useMemo(() => {
    if (field.currency) {
      return MoneyField;
    }
    if (field.percent) {
      return PercentField;
    }
    return TextField;
  }, [field.currency, field.percent]);

  const isDisabled = field.disabled || disabled;

  return (
    <FormControl
      sx={{
        position: 'relative',
      }}
      className={clsx({
        readOnly,
        editable: !readOnly,
        currencyInput: field.currency,
        phoneNumber: readOnly && field.phoneNumber,
        email: readOnly && field.email,
        'sentry-mask': field.private || field.phoneNumber || field.email,
        // MuiDisabled: readOnly || field.disabled,
      })}
      error={error || !!formikMeta.error}
      id={field.id}
      fullWidth
      size={field.size || 'small'}
    >
      <ShowIfDebugWrapper>
        <Typography fontSize={12}>{field.id}</Typography>
      </ShowIfDebugWrapper>
      <Field
        id={`${field.id}-input`} // needs to be different to parent so label "for" attribute is attached to the input
        fullWidth
        error={error || !!formikMeta.error}
        component={customTextField}
        label={loading ? `${field.label} (loading...)` : field.label}
        disabled={isDisabled}
        variant="outlined"
        multiline={options.multiline || field.multiline}
        size="small"
        onSubmit={(e: SubmitEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onChange={(e: SaltusAny) => {
          if (field.requireSubmit) {
            setControlledValue(e.target.value);
          } else {
            formikHelpers.setValue(e.target.value);
          }
        }}
        onKeyPress={(ev: SaltusAny) => {
          if (ev.key === 'Enter') {
            ev.preventDefault();
          }
        }}
        inputProps={{
          readOnly,
          multiline: options?.multiline || field.multiline,
          variant: 'outlined',
          'data-test': field.id,
          rows: field.minRows,
          maxLength: field.charLimit || undefined,
          placeholder: field.placeholder,
          size: 'small',
        }}
        InputLabelProps={{
          htmlFor: `${field.id}-input`,
          shrink: field.labelOnTop || undefined,
          fontWeight: field.boldLabel ? 600 : undefined,
        }}
        validate={(val: SaltusAny) => {
          if (typeof field.validation === 'function') {
            return field.validation(val);
          }
          return field.validation;
        }}
        name={formikField.name}
        minRows={field.minRows}
        value={valueIncludingEmpty}
        data-testmui={field.label}
        onBlur={(x: SaltusAny) => {
          formikField.onBlur(x);
          if (onBlur) {
            onBlur(x);
          }
        }}
        onClick={
          readOnly && (field.phoneNumber || field.email)
            ? () => {
                if (field.email) {
                  window.open(`mailto:${valueIncludingEmpty}`);
                } else {
                  window.open(`tel:${valueIncludingEmpty}`);
                }
              }
            : undefined
        }
        {...props}
        decimalPlaces={customTextField !== TextField ? field.decimals : undefined}
      />
      {field.showClearButton && actualValue && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            if (field.requireSubmit) {
              setControlledValue('');
            } else {
              setControlledValue('');
              formikHelpers.setValue('');
            }
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            minWidth: 'auto',
          }}
        >
          CLEAR
        </Button>
      )}
      {field.showCounter && field.charLimit ? (
        <Typography
          color={field.charLimit - (actualValue || '').length > 0 ? 'primary' : 'error'}
        >
          Character limit:{(valueIncludingEmpty || '').length}/{field.charLimit}
        </Typography>
      ) : null}
      {formikMeta.error ? (
        <Typography color="error">{formikMeta.error}</Typography>
      ) : null}
      {field.requireSubmit &&
        !formikMeta.error &&
        (field.optional || actualValue || formikField.value) && (
          <Box mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (field.currency || field.number || field.percent) {
                  handleFieldSubmit(formikField.value);
                } else {
                  handleFieldSubmit(actualValue || formikField.value || null);
                }
              }}
            >
              {actualValue || formikField.value ? 'Confirm' : 'Skip'}
            </Button>
          </Box>
        )}
    </FormControl>
  );
}
